import { err, isErr, ok, type Result } from '@nx-paeonia/utils-result';
import type { ApiError, RequestOptions } from './http.js';

export const sectionFetch = async <Target extends HTMLElement | string>(
  url: string,
  options: RequestOptions = {},
): Promise<Result<HTMLElement, ApiError>> => {
  const { timeout = 10_000, retries = 0, ...fetchOptions } = options;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          ...fetchOptions.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 422 && errorData.errors) {
          return err({
            type: 'ValidationError',
            errors: errorData.errors,
          });
        }

        return err({
          type: 'HttpError',
          status: response.status,
          message: errorData.message || response.statusText,
        });
      }

      const data = await response.text();
      const tempElement = document.createElement('div');
      tempElement.innerHTML = data;

      return ok(tempElement.firstChild as HTMLElement);
    } catch (error) {
      if (attempt === retries) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            return err({ type: 'NetworkError', message: 'Request timeout' });
          }

          return err({ type: 'NetworkError', message: error.message });
        }

        return err({ type: 'NetworkError', message: 'Unknown error occurred' });
      }

      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }

  return err({ type: 'NetworkError', message: 'Max retries exceeded' });
};

export const renderSection = async <Target extends HTMLElement | string>(
  url: string,
  target: Target,
  options: RequestOptions = {},
): Promise<Result<void, ApiError>> => {
  const html = await sectionFetch(url, options);
  if (isErr(html)) {
    return html;
  }

  if (typeof target === 'string') {
    const element = document.querySelector<HTMLElement>(target);
    if (!element) {
      return err({ type: 'ParseError', message: `Element not found for selector: ${target}` });
    }

    element.replaceWith(html.data);
  } else if (target instanceof HTMLElement) {
    target.replaceWith(html.data);
  } else {
    return err({ type: 'ParseError', message: 'Target must be a string selector or an HTMLElement' });
  }

  return ok(void 0);
};

export const renderHtmlSection = <Target extends HTMLElement | string>(
  target: Target,
  html: string,
  options: { elementToPick?: string } = {},
): Result<void, ApiError> => {
  let renderedHTML: HTMLElement = document.createElement('div');
  renderedHTML.innerHTML = html;

  if (options.elementToPick) {
    const element = renderedHTML.querySelector<Target extends HTMLElement ? Target : HTMLElement>(options.elementToPick);

    if (!element) {
      return err({ type: 'ParseError', message: `Element not found for selector: ${options.elementToPick}` });
    }

    renderedHTML = element;
  }

  if (typeof target === 'string') {
    const element = document.querySelector<HTMLElement>(target);
    if (!element) {
      return err({ type: 'ParseError', message: `Element not found for selector: ${target}` });
    }

    element.replaceWith(renderedHTML);
  } else if (target instanceof HTMLElement) {
    target.replaceWith(renderedHTML);
  } else {
    return err({ type: 'ParseError', message: 'Target must be a string selector or an HTMLElement' });
  }

  return ok(void 0);
};
